import React, { useState } from "react";
import { Card, Button, Field, TextArea, Alert, Label } from "@/components/ui";
import { fileToBase64 } from "@/lib/helpers";
import { Upload } from "lucide-react";

// Owns the upload + manual-entry form state. Parent supplies onUpload(file) and
// onCreate({ provider, amount_billed, description }); both throw on failure.
export function NewClaimForm({ onUpload, onCreate }) {
  const [provider, setProvider] = useState("");
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setErr(null); setUploading(true);
    try {
      const file_base64 = await fileToBase64(file);
      const mime_type = file.type === "application/pdf" ? "application/pdf" : file.type === "image/png" ? "image/png" : "image/jpeg";
      await onUpload({ file_base64, mime_type });
    } catch (ex) { setErr(ex?.message || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submitManual = async () => {
    setErr(null); setBusy(true);
    try {
      const amt = parseFloat(amount);
      if (isNaN(amt)) throw new Error("Enter a valid amount");
      await onCreate({ provider: provider.trim(), amount_billed: amt, description: desc.trim() });
      setProvider(""); setAmount(""); setDesc("");
    } catch (ex) { setErr(ex?.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Card className="mb-5">
        <Label>Upload a bill</Label>
        <label className="mt-2 flex items-center gap-3 border border-line rounded-lg p-4 bg-brand-tint cursor-pointer hover:bg-white transition-colors" data-testid="upload-eob-file">
          <Upload className="w-5 h-5 text-brand-dark" />
          <div className="flex-1">
            <div className="font-semibold text-sm text-ink">PDF or photo of the EOB / bill</div>
            <div className="text-xs text-subtle">{uploading ? "Analyzing your bill…" : "AI extracts provider, amount, and line items"}</div>
          </div>
          <input type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} disabled={uploading} data-testid="upload-status" />
        </label>
      </Card>

      <Label>Or enter details manually</Label>
      <Card className="mt-2">
        <Field label="Provider" data-testid="eob-provider-input" value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="Mercy General Hospital" />
        <Field label="Amount billed ($)" data-testid="eob-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="1250.00" />
        <TextArea label="Description" data-testid="eob-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3}
          placeholder="Level 5 ER visit, chest pain, same-day duplicate line item for IV administration." />
        <Button data-testid="submit-eob-button" onClick={submitManual} loading={busy} disabled={!provider.trim() || !amount.trim() || desc.trim().length < 10}>Analyze manually</Button>
      </Card>
      <Alert testid="eob-error">{err}</Alert>
    </>
  );
}
